
import React, { useEffect, useState } from 'react';
import ChatPanel from "@/components/ChatPanel";
import { InsuranceAnalysisResult, Language } from '@/types/insurance';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import SendOfferModal from '@/components/SendOfferModal';
import { useToast } from "@/hooks/use-toast";

interface DesktopBuilderLayoutProps {
  view: "preview" | "code";
  setView: (view: "preview" | "code") => void;
  emailContent: string;
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
  isStreaming: boolean;
  streamingCode: string;
  isInsuranceOffer: boolean;
  isInsuranceComparison: boolean;
  analysisResult: InsuranceAnalysisResult | null;
  detectedLanguage: Language;
  initialPrompt: string | null;
  handleEmailGenerated: (content: string, isOffer?: boolean, isComparison?: boolean, analysis?: any) => void;
  handleStreamingCodeUpdate: (html: string) => void;
  handleStreamingComplete: (finalHtml: string) => void;
  currentChatId: string | undefined;
  isOfferModalOpen: boolean;
  setIsOfferModalOpen: (open: boolean) => void;
  offerUrl: string;
  offerTitle: string;
  userEmail: string | undefined;
  containerRef?: React.RefObject<HTMLDivElement>;
  triggerTemplateGeneration: (concept: string) => Promise<void>;
  children?: React.ReactNode;
}

const DesktopBuilderLayout: React.FC<DesktopBuilderLayoutProps> = ({
  view,
  setView,
  emailContent,
  isGenerating,
  setIsGenerating,
  isStreaming,
  streamingCode,
  isInsuranceOffer,
  isInsuranceComparison,
  analysisResult,
  detectedLanguage,
  initialPrompt,
  handleEmailGenerated,
  handleStreamingCodeUpdate,
  handleStreamingComplete,
  currentChatId,
  isOfferModalOpen,
  setIsOfferModalOpen,
  offerUrl,
  offerTitle,
  userEmail,
  containerRef,
  triggerTemplateGeneration,
  children
}) => {
  const { toast } = useToast();
  const [isEventListenersRegistered, setIsEventListenersRegistered] = useState(false);
  
  // Listen for template generation events
  useEffect(() => {
    // Only register event listeners once
    if (isEventListenersRegistered) return;
    
    const handleTemplateGenerationStarted = (event: CustomEvent) => {
      console.log("[DEBUG] DesktopBuilderLayout - Template generation started event received:", event.detail);
      setIsGenerating(true);
      setView("code");
    };
    
    const handleTemplateGenerationComplete = (event: CustomEvent) => {
      console.log("[DEBUG] DesktopBuilderLayout - Template generation complete event received");
      if (event.detail?.content) {
        console.log("[DEBUG] DesktopBuilderLayout - Processing generated template content");
        handleEmailGenerated(event.detail.content);
        
        // Switch to preview after a short delay
        setTimeout(() => {
          setView("preview");
        }, 1000);
      }
    };
    
    const handleTemplateGenerationFailed = (event: CustomEvent) => {
      console.log("[DEBUG] DesktopBuilderLayout - Template generation failed event received:", event.detail?.error);
      toast({
        title: "Fehler",
        description: "Die Template-Generierung ist fehlgeschlagen: " + (event.detail?.error || "Unbekannter Fehler"),
        variant: "destructive"
      });
      setIsGenerating(false);
    };
    
    window.addEventListener('template-generation-started', handleTemplateGenerationStarted as EventListener);
    window.addEventListener('template-generation-complete', handleTemplateGenerationComplete as EventListener);
    window.addEventListener('template-generation-failed', handleTemplateGenerationFailed as EventListener);
    
    setIsEventListenersRegistered(true);
    
    return () => {
      window.removeEventListener('template-generation-started', handleTemplateGenerationStarted as EventListener);
      window.removeEventListener('template-generation-complete', handleTemplateGenerationComplete as EventListener);
      window.removeEventListener('template-generation-failed', handleTemplateGenerationFailed as EventListener);
    };
  }, [handleEmailGenerated, toast, setIsGenerating, setView, isEventListenersRegistered]);

  return (
    <>
      <ChatPanel
        onEmailGenerated={handleEmailGenerated}
        onStreamingCodeUpdate={handleStreamingCodeUpdate}
        onStreamingComplete={handleStreamingComplete}
        initialPrompt={initialPrompt}
        chatId={currentChatId}
        key={currentChatId || 'new-chat'}
        isBuilderPage={true}
        setIsGenerating={setIsGenerating}
        triggerTemplateGeneration={triggerTemplateGeneration}
      />
      
      {/* Render children (template history) if provided */}
      {children}
      
      <Dialog open={isOfferModalOpen} onOpenChange={setIsOfferModalOpen}>
        <DialogContent className="bg-gradient-to-br from-gray-950 to-gray-950/95 border border-gray-800/50 text-white sm:max-w-md">
          <SendOfferModal
            offerUrl={offerUrl}
            offerTitle={offerTitle}
            onClose={() => setIsOfferModalOpen(false)}
            userEmail={userEmail}
            isOpen={isOfferModalOpen}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DesktopBuilderLayout;
